<script setup lang="ts">
import { onMounted, computed, shallowRef } from 'vue'
import {
  THEME_PREFERENCES,
  themePreferenceIcon,
  useThemePreference
} from '~/shared-kernel/presentation/composables/useThemePreference'

const { t } = useI18n()
const isMounted = shallowRef(false)
const { preference, setThemePreference } = useThemePreference()

// Browser storage is unavailable during SSR. Keep the first client render
// identical to the server, then expose the persisted preference after mounting.
const currentMode = computed(() => isMounted.value ? preference.value : 'system')
const nextMode = computed(() => THEME_PREFERENCES[
  (THEME_PREFERENCES.indexOf(currentMode.value) + 1) % THEME_PREFERENCES.length
] ?? 'light')
const iconName = computed(() => themePreferenceIcon(currentMode.value))
const buttonLabel = computed(() => t('theme.toggleLabel', { current: t(`theme.${currentMode.value}`), next: t(`theme.${nextMode.value}`) }))

function cycleTheme() {
  setThemePreference(nextMode.value)
}

onMounted(() => {
  isMounted.value = true
})
</script>

<template>
  <!-- UTooltip keeps the app typeface; a native title tooltip is drawn by the OS in its own font. -->
  <UTooltip :text="buttonLabel">
    <button class="theme-toggle" type="button" :aria-label="buttonLabel" @click="cycleTheme">
      <UIcon v-if="isMounted" :key="iconName" :name="iconName" aria-hidden="true" />
      <span class="theme-toggle__sr">{{ buttonLabel }}</span>
    </button>
  </UTooltip>
</template>

<style scoped>
.theme-toggle {
  display: inline-grid;
  width: 2.8rem;
  height: 2.8rem;
  place-items: center;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-pill);
  background: color-mix(in oklch, var(--tq-bg-muted) 82%, transparent);
  color: var(--tq-text);
  cursor: pointer;
  transition: transform 180ms var(--tq-ease-out), background 180ms var(--tq-ease-out), border-color 180ms var(--tq-ease-out);
}

.theme-toggle:hover {
  border-color: color-mix(in oklch, var(--tq-color-teal) 42%, var(--tq-border));
  background: color-mix(in oklch, var(--tq-color-teal) 12%, var(--tq-bg-elevated));
  transform: translateY(-1px);
}

.theme-toggle :deep(.iconify) {
  width: 1.12rem;
  height: 1.12rem;
}

.theme-toggle__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
